import React from 'react';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  useEntity,
  useEntityOwnership,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { isAdminRole } from '../../utils/roles';
import { ManagedComponentsResolver } from '../../utils/managedComponents';
import { DeleteDialog, type DeleteScope } from './DeleteDialog';

/**
 * Does this Template actually accept a `delete_scope` parameter?
 *
 * Searched recursively rather than read from a fixed path: the parameter lives
 * inside `dependencies.deployment_intent.oneOf[].properties` on the delete
 * branch, and a schema is free to nest it differently. A recursive search for
 * the key under any `properties` object is stable across that, and a false
 * negative is safe -- it means we send nothing and show the destructive
 * confirmation, which every version of the Template handles correctly.
 */
export function templateDeclaresDeleteScope(template: any): boolean {
  const seen = new Set<any>();
  const walk = (node: any): boolean => {
    if (!node || typeof node !== 'object' || seen.has(node)) return false;
    seen.add(node);
    if (node.properties && typeof node.properties === 'object') {
      if (Object.prototype.hasOwnProperty.call(node.properties, 'delete_scope')) {
        return true;
      }
    }
    return Object.values(node).some(walk);
  };
  return walk(template?.spec?.parameters);
}

interface DeleteComponentButtonProps {
  entity?: any;
  size?: 'small' | 'medium';
  render?: 'icon' | 'button';
  fullWidth?: boolean;
  label?: string;
}

const DeleteComponentButtonWithContext = ({ size = 'small', render = 'icon', fullWidth, label }: Omit<DeleteComponentButtonProps, 'entity'>) => {
  const { entity } = useEntity();
  return <DeleteComponentButtonInternal  entity={entity} size={size} render={render} fullWidth={fullWidth} label={label} />;
};

const DeleteComponentButtonInternal = ({ entity, size = 'small', render = 'icon', fullWidth, label }: Required<Pick<DeleteComponentButtonProps, 'entity'>> & Omit<DeleteComponentButtonProps, 'entity'>) => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { isOwnedEntity } = useEntityOwnership();
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
  const [isExecuting, setIsExecuting] = React.useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  // 'unknown' until the live Template has been read. Never assume support: the
  // safe-looking option must not be offered by a UI that a Template cannot honour.
  const [scopeSupport, setScopeSupport] =
    React.useState<'unknown' | 'yes' | 'no'>('unknown');

  // Component type detection + template ref resolution is fully driven
  // by app-config (`scaffolder.managedComponents`). Adding a new module
  // requires no code change here.
  const managed = React.useMemo(
    () => new ManagedComponentsResolver(configApi),
    [configApi],
  );
  const detectedType = React.useMemo(
    () => managed.detectType(entity),
    [managed, entity],
  );
  const componentType = detectedType?.type ?? null;

  // A database-only component has no application to separate from its data, so
  // the scope choice is meaningless for it: offering "delete the application,
  // keep my database" would take nothing away while still removing the catalog
  // entry, so deleting one removes it completely either way.
  //
  // Detected from the same annotation the Update/Restart buttons key on: no
  // container image means no application. Resolved from the entity, so it needs
  // no wait for the Template probe below, and forcing 'no' makes DeleteDialog
  // show the plain "this removes everything" confirmation instead of the radio
  // group.
  const isDatabaseOnly = !managed.hasContainerImage(entity);
  const effectiveScopeSupport: 'unknown' | 'yes' | 'no' = isDatabaseOnly
    ? 'no'
    : scopeSupport;
  // One template, one delete path: the main template's `delete` intent removes
  // the Kubernetes resources AND the catalog entry with its Location. The
  // separate admin-only deletion template is gone -- it existed to be
  // admin-only by being unreadable, which stopped being true once students
  // needed it, and it left two k8s:delete manifests to keep in step.
  const deleteTemplateRef = detectedType?.templates.primary;

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const ownershipEntityRefs = identity.ownershipEntityRefs ?? [];
        const admin = isAdminRole(ownershipEntityRefs, configApi);
        if (mounted) setIsAdmin(admin);
      } catch (_e) {
        if (mounted) setIsAdmin(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [identityApi, configApi]);

  // Whether the Template this button will actually run declares `delete_scope`.
  //
  // This MUST come from the live Template, not from build-time app-config. The
  // two ship on different clocks: the frontend image updates the moment ArgoCD
  // restarts it, while an edited Template file is only re-read when its Location
  // next processes -- `catalog.processingInterval`, up to 90 minutes. A flag
  // baked into the image would claim the safe option exists while the Template
  // still in the catalog knows nothing about it, and that Template's delete step
  // destroys the database unconditionally. The student would tick "keep my
  // database (recommended)" and lose their coursework.
  //
  // Read on open rather than on mount so it reflects the Template as of the
  // moment the user is about to act, and so the common case (never opening the
  // dialog) costs no request.
  React.useEffect(() => {
    if (!dialogOpen || !deleteTemplateRef) return undefined;
    let mounted = true;
    setScopeSupport('unknown');
    (async () => {
      try {
        const template = await catalogApi.getEntityByRef(deleteTemplateRef);
        if (mounted) {
          setScopeSupport(templateDeclaresDeleteScope(template) ? 'yes' : 'no');
        }
      } catch (e) {
        // Fail safe, not open: show the destructive confirmation and send no
        // delete_scope, which every version of the Template handles correctly.
        console.warn('Could not read the delete template; assuming no delete_scope', e);
        if (mounted) setScopeSupport('no');
      }
    })();
    return () => {
      mounted = false;
    };
  }, [dialogOpen, deleteTemplateRef, catalogApi]);

  const isOwner = React.useMemo(() => {
    try {
      return !!(entity && isOwnedEntity(entity));
    } catch {
      return false;
    }
  }, [entity, isOwnedEntity]);

  const canDelete = isAdmin || isOwner;

  // Note: Removed localStorage-based deletion tracking
  // The button stays enabled so users can retry if deletion fails partway
  // The catalog entry removal (separate step) is the true indicator of deletion

  // The confirmation lives in DeleteDialog rather than window.confirm: the
  // choice between keeping and wiping the database cannot be expressed as a
  // yes/no, and a destructive default hidden behind OK is exactly what this
  // change exists to remove.
  const handleDelete = async (scope?: DeleteScope) => {
    if (!entity) return;
    try {
      setIsExecuting(true);

      const { baseName, targetUser } = managed.parseEntityName(entity);

      const namespace = entity.metadata.annotations?.['kubernetes.io/namespace'] as string | undefined;
      if (!namespace) {
        alert('Cannot delete: Kubernetes namespace not found in entity annotations.');
        return;
      }

      if (!deleteTemplateRef) return; // Should be unreachable; render gate below
      const templateRef = deleteTemplateRef;

      // No entityUid/locationTarget any more. The templates no longer register a
      // catalog Location, so there is none to delete: the delete flow removes the
      // component's file from the metadata repository, and the push refreshes the
      // provider's glob Location, which drops the entity along with the file.
      const values: Record<string, any> = {
        deployment_intent: 'delete',
        name: baseName,
        namespace,
      };

      // Only when the live Template declares the parameter. Omitting it is the
      // safe branch there anyway -- the Template tests for the destructive value
      // rather than the absence of the safe one -- so a Template that predates
      // the parameter keeps the database rather than failing.
      if (effectiveScopeSupport === 'yes' && scope) {
        values.delete_scope = scope;
      }

      if (targetUser) {
        values.targetUser = targetUser;
      }

      const response = await scaffolderApi.scaffold({
        templateRef,
        values,
      });

      if (response.taskId) {
        // Note: Don't set localStorage flag here - it should only be set after task succeeds
        // The flag was causing issues when deletion failed partway, leaving the button disabled
        // with orphaned resources. Users can retry deletion if it fails.
        window.location.href = `/create/tasks/${response.taskId}`;
      }
    } catch (e) {
      console.error('Failed to trigger component deletion:', e);
      alert('Failed to delete this component. Please try again.');
    } finally {
      setIsExecuting(false);
      setDialogOpen(false);
    }
  };

  if (!entity || !componentType || !deleteTemplateRef || !canDelete) {
    return null;
  }

  const tooltip = isExecuting ? 'Deleting...' : 'Delete Application Resources';

  const dialog = (
    <DeleteDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      onConfirm={handleDelete}
      applicationName={entity.metadata.name as string}
      isExecuting={isExecuting}
      scopeSupport={effectiveScopeSupport}
    />
  );

  if (render === 'button') {
    return (
      <>
        <Tooltip title={tooltip}>
          <span>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setDialogOpen(true)}
              disabled={isExecuting}
              fullWidth={!!fullWidth}
            >
              {label || 'Delete'}
            </Button>
          </span>
        </Tooltip>
        {dialog}
      </>
    );
  }

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            color="secondary"
            onClick={() => setDialogOpen(true)}
            size={size}
            disabled={isExecuting}
          >
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>
      {dialog}
    </>
  );
};

export const DeleteComponentButton = ({ entity: propEntity, size = 'small', render = 'icon', fullWidth, label }: DeleteComponentButtonProps) => {
  if (propEntity) {
    return <DeleteComponentButtonInternal entity={propEntity} size={size} render={render} fullWidth={fullWidth} label={label} />;
  }
  return <DeleteComponentButtonWithContext size={size} render={render} fullWidth={fullWidth} label={label} />;
};


